import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { LogOut, Menu, X, Bell, UserCog, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '@context/AuthContext';
import { useIdleTimer } from '../hooks/useIdleTimer';
import IdleTimeoutModal from './shared/IdleTimeoutModal';
import { visibleNav } from '../lib/nav';

// Import custom logos
import NexusShield from '@assets/images/NexusShield';
import NexusFullLogo from '@assets/images/NexusFullLogo';

// Import components
import DashboardContent from './DashboardContent';
import ClientsContent from './clients/ClientsContent';
import ClientDetailsView from './clients/ClientDetailsView';
import ClientFormPage from './clients/ClientFormPage';
import ClientOnboardingWizard from './clients/ClientOnboardingWizard';
import DependantFormPage from './clients/dependants/DependantFormPage';
import KycUploadPage from './clients/KycUploadModal';
import KycHistoryPage from './clients/KycHistoryModal';
import CompaniesContent from './companies/CompaniesContent';
import ProductsContent from './products/ProductsContent';
import SystemUsersContent from './systemowners/SystemUsersContent';
import KycSettings from './kyc/KycSettings';
import KycQueuePage from './kyc/KycQueuePage';
import KycForm from './kyc/KycForm';

// Import Asset components
import AssetsContent from './assets/AssetsContent';
import AssetFormPage from './assets/AssetFormPage';
import AssetDetailsView from './assets/AssetDetailsView';
import AssetRequirementsContent from "./assets/AssetRequirementsContent";
import AssetRequirementForm from './assets/AssetRequirementForm';
import ProfilePage from './settings/ProfilePage';
import RolesContent from './roles/RolesContent';
import AuditLogsPage from './audit/AuditLogsPage';

// Import Data Quality components
import DataQualityDashboard from './data-quality/DataQualityDashboard';
import DuplicatesContent from './data-quality/DuplicatesContent';
import MergeWorkflow from './data-quality/MergeWorkflow';
import MergeHistory from './data-quality/MergeHistory';
import CompletenessContent from './data-quality/CompletenessContent';
import CompletenessDetail from './data-quality/CompletenessDetail';
import IssuesContent from './data-quality/IssuesContent';
import CleanupTools from './data-quality/CleanupTools';
import CleanupMissingField from './data-quality/CleanupMissingField';
import CleanupStaleRecords from './data-quality/CleanupStaleRecords';
import CompletenessRules from './data-quality/CompletenessRules';
import CompletenessRuleForm from './data-quality/CompletenessRuleForm';
import { hasKeycloakSession, logout as keycloakLogout } from '../auth/keycloak';

const Dashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [activeMenu, setActiveMenu] = useState('dashboard');
    const [openSections, setOpenSections] = useState({});
    const [productFilter, setProductFilter] = useState(null);
    const [expiryBannerDismissed, setExpiryBannerDismissed] = useState(false);

    const { user, logout, hasPermission } = useAuth();
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Idle timeout: auto-logout after 15 minutes of inactivity
    const handleIdleLogout = useCallback(() => {
        logout('You have been logged out due to inactivity.');
        navigate('/login');
    }, [logout, navigate]);

    const { showWarning, countdown, stayLoggedIn } = useIdleTimer(handleIdleLogout);

    // Active menu detection + auto-expand parent sections
    useEffect(() => {
        const path = location.pathname;

        if (path === '/' || path === '/dashboard') setActiveMenu('dashboard');
        else if (path.startsWith('/clients')) setActiveMenu('clients');
        else if (path.startsWith('/companies')) setActiveMenu('companies');
        else if (path.startsWith('/products')) setActiveMenu('products');
        else if (path.startsWith('/compliance/kyc/queue')) setActiveMenu('kyc-queue');
        else if (path.startsWith('/compliance/kyc/settings')) setActiveMenu('kyc-settings');
        else if (path.startsWith('/compliance/assets/requirements')) setActiveMenu('asset-requirements');
        else if (path.startsWith('/compliance/assets')) setActiveMenu('asset-list');
        else if (path === '/system/users' || path.startsWith('/system/users/')) setActiveMenu('system-users');
        else if (path === '/system/roles' || path.startsWith('/system/roles/')) setActiveMenu('system-roles');
        else if (path === '/system/audit-logs') setActiveMenu('audit-logs');
        else if (path === '/system/settings') setActiveMenu('settings');
        else if (path === '/data-quality/dashboard') setActiveMenu('dq-dashboard');
        else if (path === '/data-quality/duplicates') setActiveMenu('dq-duplicates');
        else if (path.startsWith('/data-quality/merge/history')) setActiveMenu('dq-merge-history');
        else if (path.startsWith('/data-quality/merge')) setActiveMenu('dq-duplicates');
        else if (path.startsWith('/data-quality/completeness')) setActiveMenu('dq-completeness');
        else if (path === '/data-quality/issues') setActiveMenu('dq-issues');
        else if (path.startsWith('/data-quality/cleanup')) setActiveMenu('dq-cleanup');
        else if (path.startsWith('/data-quality/rules')) setActiveMenu('dq-rules');
        else setActiveMenu(null);

        // Auto-expand parent sections when a child route is active
        if (path.startsWith('/compliance/kyc')) {
            setOpenSections(prev => ({ ...prev, kyc: true }));
        }
        if (path.startsWith('/compliance/assets')) {
            setOpenSections(prev => ({ ...prev, 'assets-parent': true }));
        }
        if (path.startsWith('/data-quality')) {
            setOpenSections(prev => ({ ...prev, 'data-quality': true }));
        }
    }, [location.pathname]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setUserDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Get current page title
    const getPageTitle = () => {
        const path = location.pathname;
        if (path === '/' || path === '/dashboard') return 'Dashboard';
        if (path === '/clients') return 'Clients';
        if (path === '/clients/new') return 'New Client';
        if (path.match(/\/clients\/\d+\/dependants\/new/)) return 'Add Dependant';
        if (path.match(/\/clients\/\d+\/dependants\/\d+\/edit/)) return 'Edit Dependant';
        if (path.match(/\/clients\/\d+\/edit/)) return 'Edit Client';
        if (path.match(/\/clients\/\d+\/kyc/)) return 'Client KYC';
        if (path.match(/\/clients\/\d+\/assets/)) return 'Client Assets';
        if (path.match(/\/clients\/\d+/)) return 'Client Details';
        if (path === '/companies') return 'Companies';
        if (path === '/products') return 'Products';
        if (path === '/compliance/kyc/queue') return 'KYC Queue';
        if (path === '/compliance/kyc/settings') return 'KYC Settings';
        if (path === '/compliance/kyc/settings/new') return 'Add KYC Requirement';
        if (path.match(/\/compliance\/kyc\/settings\/\d+\/edit/)) return 'Edit KYC Requirement';
        if (path === '/compliance/assets') return 'Asset List';
        if (path === '/compliance/assets/new') return 'Add Asset';
        if (path === '/compliance/assets/requirements') return 'Asset Requirements';
        if (path === '/compliance/assets/requirements/new') return 'Add Asset Requirement';
        if (path.match(/\/compliance\/assets\/requirements\/\d+\/edit/)) return 'Edit Asset Requirement';
        if (path.match(/\/compliance\/assets\/\d+\/edit/)) return 'Edit Asset';
        if (path.match(/\/compliance\/assets\/\d+/)) return 'Asset Details';
        if (path === '/system/users') return 'System Users';
        if (path === '/system/roles') return 'Roles & Permissions';
        if (path === '/system/settings') return 'Settings';
        if (path === '/system/audit-logs') return 'Audit Logs';
        if (path === '/data-quality/dashboard') return 'Data Quality Dashboard';
        if (path === '/data-quality/duplicates') return 'Duplicate Detection';
        if (path === '/data-quality/merge/history') return 'Merge History';
        if (path.match(/\/data-quality\/merge\/\d+/)) return 'Merge Workflow';
        if (path.match(/\/data-quality\/completeness\/client\/\d+/)) return 'Completeness Detail';
        if (path === '/data-quality/completeness') return 'Profile Completeness';
        if (path === '/data-quality/issues') return 'Data Quality Issues';
        if (path === '/data-quality/cleanup/missing-field') return 'Missing Fields';
        if (path === '/data-quality/cleanup/stale') return 'Stale Records';
        if (path === '/data-quality/cleanup') return 'Cleanup Tools';
        if (path === '/data-quality/rules/new') return 'Add Completeness Rule';
        if (path.match(/\/data-quality\/rules\/\d+\/edit/)) return 'Edit Completeness Rule';
        if (path === '/data-quality/rules') return 'Completeness Rules';
        return 'Dashboard';
    };

    const visibleSections = visibleNav(hasPermission);

    // Toggle expandable section
    const toggleSection = (sectionId) => {
        setOpenSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
    };

    // Handle menu click with navigation
    const handleMenuClick = (item) => {
        setProductFilter(null);
        navigate(item.path);
    };

    // Check if a parent's child is active
    const isChildActive = (item) => {
        if (!item.children) return false;
        return item.children.some(child => child.id === activeMenu);
    };

    // Navigation handler for companies to products
    const handleNavigateToProducts = (companyId, companyName) => {
        setProductFilter({ companyId, companyName });
        navigate('/products');
    };

    // Back navigation handler
    const handleBackNavigation = () => {
        navigate('/companies');
        setProductFilter(null);
    };

    // Handle logout
    const handleLogout = () => {
        logout();
        // Microsoft users: end the Keycloak session too (redirects to /login)
        if (hasKeycloakSession()) {
            keycloakLogout();
            return;
        }
        navigate('/login');
    };

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Sidebar */}
            <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 transition-all duration-300 flex flex-col shadow-sm`}>
                {/* Sidebar Header */}
                <div className="h-[73px] flex items-center justify-center border-b border-gray-200">
                    <div className="px-4">
                        {sidebarOpen ? (
                            <NexusFullLogo className="h-6" />
                        ) : (
                            <NexusShield className="w-8 h-8" />
                        )}
                    </div>
                </div>

                <nav className="flex-1 p-4 overflow-y-auto">
                    {visibleSections.map((section, sIdx) => (
                        <div key={section.label} className={sIdx > 0 ? 'mt-4' : ''}>
                            {sidebarOpen && (
                                <p className="px-4 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                    {section.label}
                                </p>
                            )}
                            {!sidebarOpen && sIdx > 0 && (
                                <div className="mx-3 mb-2 border-t border-gray-200" />
                            )}
                            {section.items.map((item) => (
                                <div key={item.id} className="mb-1">
                                    {item.children ? (
                                        // Expandable parent item
                                        <>
                                            <button
                                                onClick={() => toggleSection(item.id)}
                                                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition ${
                                                    isChildActive(item)
                                                        ? 'bg-blue-50 text-blue-600'
                                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                                }`}
                                                title={!sidebarOpen ? item.label : ''}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <item.icon className="w-5 h-5" />
                                                    {sidebarOpen && <span>{item.label}</span>}
                                                </div>
                                                {sidebarOpen && (
                                                    openSections[item.id]
                                                        ? <ChevronDown className="w-4 h-4" />
                                                        : <ChevronRight className="w-4 h-4" />
                                                )}
                                            </button>
                                            {/* Children */}
                                            {sidebarOpen && openSections[item.id] && (
                                                <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
                                                    {item.children.map((child) => (
                                                        <button
                                                            key={child.id}
                                                            onClick={() => handleMenuClick(child)}
                                                            className={`w-full flex items-center px-3 py-2 rounded-lg text-sm transition ${
                                                                activeMenu === child.id
                                                                    ? 'bg-blue-50 text-blue-600 font-medium'
                                                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                                            }`}
                                                        >
                                                            {child.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        // Regular nav link
                                        <button
                                            onClick={() => handleMenuClick(item)}
                                            className={`w-full flex items-center px-4 py-3 rounded-lg transition ${
                                                activeMenu === item.id
                                                    ? 'bg-blue-50 text-blue-600 border border-blue-200'
                                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                            }`}
                                            title={!sidebarOpen ? item.label : ''}
                                        >
                                            <div className="flex items-center gap-3">
                                                <item.icon className="w-5 h-5" />
                                                {sidebarOpen && <span>{item.label}</span>}
                                            </div>
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}
                </nav>

                <div className="p-4 border-t border-gray-200">
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition"
                        title={!sidebarOpen ? "Logout" : ''}
                    >
                        <LogOut className="w-5 h-5" />
                        {sidebarOpen && <span>Logout</span>}
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <header className="h-[73px] bg-white shadow-sm border-b border-gray-200 flex items-center">
                    <div className="w-full px-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => setSidebarOpen(!sidebarOpen)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition"
                                >
                                    {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                                </button>
                                <h2 className="text-2xl font-semibold text-gray-900">
                                    {getPageTitle()}
                                </h2>
                            </div>

                            <div className="flex items-center gap-4">
                                <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition">
                                    <Bell className="w-5 h-5" />
                                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                                </button>

                                <div className="relative" ref={dropdownRef}>
                                    <button
                                        onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition"
                                    >
                                        <div className="text-right">
                                            <p className="text-sm font-semibold text-gray-900">{user?.firstName} {user?.lastName}</p>
                                            <p className="text-xs text-gray-500">{user?.role}</p>
                                        </div>
                                        <div className="w-10 h-10 bg-gray-100 border border-gray-200 rounded-full flex items-center justify-center text-gray-700 font-semibold">
                                            {user?.firstName?.[0]}{user?.lastName?.[0]}
                                        </div>
                                    </button>

                                    {/* User Dropdown Menu */}
                                    {userDropdownOpen && (
                                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                                            <div className="px-4 py-3 border-b border-gray-200">
                                                <p className="text-sm font-semibold text-gray-900">{user?.firstName} {user?.lastName}</p>
                                                <p className="text-xs text-gray-500 mt-1">{user?.email}</p>
                                                <p className="text-xs text-gray-500">{user?.role}</p>
                                            </div>

                                            <button
                                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                                onClick={() => {
                                                    setUserDropdownOpen(false);
                                                    navigate('/system/settings');
                                                }}
                                            >
                                                <UserCog className="w-4 h-4" />
                                                Profile Settings
                                            </button>

                                            <div className="border-t border-gray-200 mt-2 pt-2">
                                                <button
                                                    onClick={handleLogout}
                                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                                >
                                                    <LogOut className="w-4 h-4" />
                                                    Logout
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Content Area with Routes */}
                <main className="flex-1 p-6 overflow-y-auto bg-gray-50">
                    {/* Password Expiry Warning Banner */}
                    {user?.passwordAgeDays >= 83 && !expiryBannerDismissed && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                                <p className="text-sm text-amber-800">
                                    Your password expires in <strong>{90 - user.passwordAgeDays}</strong> days.{' '}
                                    <button
                                        onClick={() => navigate('/system/settings?tab=password')}
                                        className="text-amber-900 underline font-medium hover:text-amber-700"
                                    >
                                        Change it now
                                    </button>
                                </p>
                            </div>
                            <button
                                onClick={() => setExpiryBannerDismissed(true)}
                                className="text-amber-600 hover:text-amber-800 p-1"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    <Routes>
                        {/* Dashboard — always accessible */}
                        <Route path="/" element={<DashboardContent />} />
                        <Route path="/dashboard" element={<DashboardContent />} />

                        {/* Client Management */}
                        <Route path="/clients" element={hasPermission('CLIENTS:READ') ? <ClientsContent type="all-clients" /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/clients/new" element={hasPermission('CLIENTS:CREATE') ? <ClientOnboardingWizard /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/clients/:clientId/dependants/new" element={hasPermission('DEPENDANTS:CREATE') ? <DependantFormPage /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/clients/:clientId/dependants/:dependantId/edit" element={hasPermission('DEPENDANTS:UPDATE') ? <DependantFormPage /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/clients/:clientId/kyc/submit" element={hasPermission('KYC:CREATE') ? <KycUploadPage /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/clients/:clientId/kyc/:kycId/history" element={hasPermission('KYC:READ') ? <KycHistoryPage /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/clients/:id" element={hasPermission('CLIENTS:READ') ? <ClientDetailsView /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/clients/:id/edit" element={hasPermission('CLIENTS:UPDATE') ? <ClientFormPage /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/clients/:clientId/assets/new" element={hasPermission('ASSETS:CREATE') ? <AssetFormPage /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/clients/:clientId/assets/:id/edit" element={hasPermission('ASSETS:UPDATE') ? <AssetFormPage /> : <Navigate to="/dashboard" replace />} />

                        {/* Business Setup */}
                        <Route path="/companies" element={hasPermission('COMPANIES:READ') ? <CompaniesContent onNavigateToProducts={handleNavigateToProducts} /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/products" element={hasPermission('PRODUCTS:READ') ? (
                            <div>
                                {productFilter && (
                                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <button onClick={handleBackNavigation} className="text-blue-600 hover:text-blue-700 font-medium">
                                                &larr; Back to Companies
                                            </button>
                                            <span className="text-gray-500">|</span>
                                            <span className="text-gray-700">
                                                Viewing products for: <strong>{productFilter.companyName}</strong>
                                            </span>
                                        </div>
                                        <button onClick={() => setProductFilter(null)} className="text-sm text-gray-600 hover:text-gray-800">
                                            Clear Filter &times;
                                        </button>
                                    </div>
                                )}
                                <ProductsContent companyFilter={productFilter?.companyId} />
                            </div>
                        ) : <Navigate to="/dashboard" replace />} />

                        {/* Compliance — KYC */}
                        <Route path="/compliance/kyc/queue" element={hasPermission('KYC:READ') ? <KycQueuePage /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/compliance/kyc/settings" element={hasPermission('KYC_CONFIG:READ') ? <KycSettings /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/compliance/kyc/settings/new" element={hasPermission('KYC_CONFIG:CREATE') ? <KycForm /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/compliance/kyc/settings/:id/edit" element={hasPermission('KYC_CONFIG:UPDATE') ? <KycForm /> : <Navigate to="/dashboard" replace />} />

                        {/* Compliance — Assets */}
                        <Route path="/compliance/assets" element={hasPermission('ASSETS:READ') ? <AssetsContent /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/compliance/assets/new" element={hasPermission('ASSETS:CREATE') ? <AssetFormPage /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/compliance/assets/requirements" element={hasPermission('ASSET_CONFIG:READ') ? <AssetRequirementsContent /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/compliance/assets/requirements/new" element={hasPermission('ASSET_CONFIG:CREATE') ? <AssetRequirementForm /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/compliance/assets/requirements/:id/edit" element={hasPermission('ASSET_CONFIG:UPDATE') ? <AssetRequirementForm /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/compliance/assets/:id/edit" element={hasPermission('ASSETS:UPDATE') ? <AssetFormPage /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/compliance/assets/:id" element={hasPermission('ASSETS:READ') ? <AssetDetailsView /> : <Navigate to="/dashboard" replace />} />

                        {/* Data Quality / Governance */}
                        <Route path="/data-quality/dashboard" element={hasPermission('DATA_QUALITY:READ') ? <DataQualityDashboard /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/duplicates" element={hasPermission('DATA_QUALITY:READ') ? <DuplicatesContent /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/merge/history" element={hasPermission('DATA_QUALITY:MERGE') ? <MergeHistory /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/merge/:primaryId" element={hasPermission('DATA_QUALITY:MERGE') ? <MergeWorkflow /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/completeness/client/:clientId" element={hasPermission('DATA_QUALITY:READ') ? <CompletenessDetail /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/completeness" element={hasPermission('DATA_QUALITY:READ') ? <CompletenessContent /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/issues" element={hasPermission('DATA_QUALITY:READ') ? <IssuesContent /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/cleanup/missing-field" element={hasPermission('DATA_QUALITY:READ') ? <CleanupMissingField /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/cleanup/stale" element={hasPermission('DATA_QUALITY:READ') ? <CleanupStaleRecords /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/cleanup" element={hasPermission('DATA_QUALITY:WRITE') ? <CleanupTools /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/rules/new" element={hasPermission('DATA_QUALITY:ADMIN') ? <CompletenessRuleForm /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/rules/:id/edit" element={hasPermission('DATA_QUALITY:ADMIN') ? <CompletenessRuleForm /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/data-quality/rules" element={hasPermission('DATA_QUALITY:ADMIN') ? <CompletenessRules /> : <Navigate to="/dashboard" replace />} />

                        {/* System */}
                        <Route path="/system/users" element={hasPermission('USERS:READ') ? <SystemUsersContent /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/system/roles" element={hasPermission('ROLES:READ') ? <RolesContent /> : <Navigate to="/dashboard" replace />} />
                        <Route path="/system/audit-logs" element={hasPermission('AUDIT:READ') ? <AuditLogsPage /> : <Navigate to="/dashboard" replace />} />

                        {/* Profile — always accessible */}
                        <Route path="/system/settings" element={<ProfilePage />} />

                        {/* Catch-all */}
                        <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                </main>
            </div>

            {/* Idle Timeout Warning Modal */}
            {showWarning && (
                <IdleTimeoutModal
                    countdown={countdown}
                    onStayLoggedIn={stayLoggedIn}
                    onLogout={handleLogout}
                />
            )}
        </div>
    );
};

export default Dashboard;
